// MaturaTrigProductSumClosedTask.js
// Zadanie maturalne – zamknięte (trygonometria):
// Wyrażenie z funkcjami sin, cos, tan kątów 30°, 45°, 60°:
//   - iloczyn ± iloczyn
//   - albo iloczyn ± pojedyncza funkcja (w dowolnej kolejności).
// Zawsze dwa składniki, przynajmniej jeden iloczyn.
// Wynik jest jedną z wartości: 0, ±1/2, ±√2/2, ±√3/2, ±√3/3, ±1.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const ROOT2 = Math.SQRT2;
const ROOT3 = Math.sqrt(3);

const gcd = (a, b) => {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
};

// uproszczenie ułamka z pierwiastkiem: r * sqrt(rad)
function makeLatexFromSymbolic(rNum, rDen, rad) {
  // rad – pod pierwiastkiem (1,2,3,4,6,9)
  if (rad === 4) {
    rNum *= 2;
    rad = 1;
  } else if (rad === 9) {
    rNum *= 3;
    rad = 1;
  }

  const g = gcd(rNum, rDen);
  rNum /= g;
  rDen /= g;

  if (rad === 1) {
    // czysto wymiernie
    if (rDen === 1) return `${rNum}`;
    return `\\dfrac{${rNum}}{${rDen}}`;
  }

  const sqrtPart = `\\sqrt{${rad}}`;
  if (rDen === 1) {
    if (rNum === 1) return sqrtPart;
    return `${rNum}${sqrtPart}`;
  }

  const numLatex = rNum === 1 ? sqrtPart : `${rNum}${sqrtPart}`;
  return `\\dfrac{${numLatex}}{${rDen}}`;
}

// wartości funkcji dla kątów 30°, 45°, 60°
// value – liczba, numLatex – podstawienie, ratNum/ratDen, rad – r * sqrt(rad)
const TRIG = {
  sin: {
    30: { value: 1 / 2, numLatex: "\\dfrac{1}{2}", ratNum: 1, ratDen: 2, rad: 1 },
    45: { value: ROOT2 / 2, numLatex: "\\dfrac{\\sqrt{2}}{2}", ratNum: 1, ratDen: 2, rad: 2 },
    60: { value: ROOT3 / 2, numLatex: "\\dfrac{\\sqrt{3}}{2}", ratNum: 1, ratDen: 2, rad: 3 },
  },
  cos: {
    30: { value: ROOT3 / 2, numLatex: "\\dfrac{\\sqrt{3}}{2}", ratNum: 1, ratDen: 2, rad: 3 },
    45: { value: ROOT2 / 2, numLatex: "\\dfrac{\\sqrt{2}}{2}", ratNum: 1, ratDen: 2, rad: 2 },
    60: { value: 1 / 2, numLatex: "\\dfrac{1}{2}", ratNum: 1, ratDen: 2, rad: 1 },
  },
  tan: {
    30: { value: ROOT3 / 3, numLatex: "\\dfrac{\\sqrt{3}}{3}", ratNum: 1, ratDen: 3, rad: 3 },
    45: { value: 1, numLatex: "1", ratNum: 1, ratDen: 1, rad: 1 },
    60: { value: ROOT3, numLatex: "\\sqrt{3}", ratNum: 1, ratDen: 1, rad: 3 },
  },
};

const FUNC_LATEX = {
  sin: "\\sin",
  cos: "\\cos",
  tan: "\\tan",
};

// znane wyniki do ABCD
const KNOWN_RESULTS = [
  { key: 0, latex: "0" },
  { key: 1 / 2, latex: "\\dfrac{1}{2}" },
  { key: -1 / 2, latex: "-\\dfrac{1}{2}" },
  { key: ROOT2 / 2, latex: "\\dfrac{\\sqrt{2}}{2}" },
  { key: -ROOT2 / 2, latex: "-\\dfrac{\\sqrt{2}}{2}" },
  { key: ROOT3 / 2, latex: "\\dfrac{\\sqrt{3}}{2}" },
  { key: -ROOT3 / 2, latex: "-\\dfrac{\\sqrt{3}}{2}" },
  { key: ROOT3 / 3, latex: "\\dfrac{\\sqrt{3}}{3}" },
  { key: -ROOT3 / 3, latex: "-\\dfrac{\\sqrt{3}}{3}" },
  { key: 1, latex: "1" },
  { key: -1, latex: "-1" },
];

const ANGLES = [30, 45, 60];
const FUNCS = ["sin", "cos", "tan"];

function snapToKnown(value) {
  const tol = 1e-6;
  for (const item of KNOWN_RESULTS) {
    if (Math.abs(value - item.key) < tol) return item;
  }
  return null;
}

function factorKey(func, angle) {
  return `${func}_${angle}`;
}

function makeFactor() {
  const func = pick(FUNCS);
  const angle = pick(ANGLES);
  const base = TRIG[func][angle];
  const { value, numLatex, ratNum, ratDen, rad } = base;
  const symbolLatex = `${FUNC_LATEX[func]}${angle}^\\circ`;
  return {
    func,
    angle,
    value,
    numLatex,
    ratNum,
    ratDen,
    rad,
    symbolLatex,
    key: factorKey(func, angle),
  };
}

function makeTerm(isProduct) {
  if (!isProduct) {
    const f = makeFactor();
    return {
      type: "single",
      value: f.value,
      latex: f.symbolLatex,
      numericLatex: f.numLatex,
      afterLatex: f.numLatex,
      symRatNum: f.ratNum,
      symRatDen: f.ratDen,
      symRad: f.rad,
      factors: [f],
    };
  }

  const f1 = makeFactor();
  let f2 = makeFactor();
  // trochę łagodzimy: unikamy tan * tan
  let guard = 0;
  while (f1.func === "tan" && f2.func === "tan" && guard < 10) {
    f2 = makeFactor();
    guard += 1;
  }

  const value = f1.value * f2.value;

  // symboliczne mnożenie: (r1 * sqrt(rad1)) * (r2 * sqrt(rad2))
  let ratNum = f1.ratNum * f2.ratNum;
  let ratDen = f1.ratDen * f2.ratDen;
  let rad = f1.rad * f2.rad; // 1,2,3,4,6,9

  const afterLatex = makeLatexFromSymbolic(ratNum, ratDen, rad);

  const latex = `${f1.symbolLatex}\\cdot${f2.symbolLatex}`;
  const numericLatex = `${f1.numLatex}\\cdot${f2.numLatex}`;

  return {
    type: "product",
    value,
    latex,
    numericLatex,
    afterLatex,
    symRatNum: ratNum,
    symRatDen: ratDen,
    symRad: rad,
    factors: [f1, f2],
  };
}

// ======================= GENERATOR =======================

function createOriginalTask() {
  const optionValues = [
    { value: 1 / 2, latex: "\\dfrac{1}{2}" },
    { value: ROOT2 / 2, latex: "\\dfrac{\\sqrt{2}}{2}" },
    { value: ROOT3 / 2, latex: "\\dfrac{\\sqrt{3}}{2}" },
    { value: 1, latex: "1" },
  ];
  const options = optionValues.map((option, index) => ({
    ...option,
    label: LETTERS[index],
    isCorrect: index === 3,
  }));

  return {
    type: "MaturaTrigProductSumClosedTask",
    data: {
      exprLatex:
        "\\sin 30^\\circ\\cdot\\cos 60^\\circ + \\sin 60^\\circ\\cdot\\cos 30^\\circ",
      exprNumericLatex:
        "\\dfrac{1}{2}\\cdot\\dfrac{1}{2} + \\dfrac{\\sqrt{3}}{2}\\cdot\\dfrac{\\sqrt{3}}{2}",
      exprAfterProductsLatex: "\\dfrac{1}{4} + \\dfrac{3}{4}",
      exprCombinedLatex: "1",
      value: 1,
      correctLatex: "1",
      options,
      correctIndex: 3,
      usedPairs: [
        { func: "sin", angle: 30, numLatex: "\\dfrac{1}{2}" },
        { func: "cos", angle: 60, numLatex: "\\dfrac{1}{2}" },
        { func: "sin", angle: 60, numLatex: "\\dfrac{\\sqrt{3}}{2}" },
        { func: "cos", angle: 30, numLatex: "\\dfrac{\\sqrt{3}}{2}" },
      ],
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 20,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  let exprLatex;
  let exprNumericLatex;
  let exprAfterProductsLatex;
  let exprCombinedLatex = null; // po sprowadzeniu do wspólnego mianownika
  let value;
  let term1;
  let term2;
  let sign;
  let usedPairsMap;
  let correct;

  // zawsze dwa składniki
  while (true) {
    usedPairsMap = new Map();

    // wzorce: product±product, product±single, single±product
    const patterns = [
      ["product", "product"],
      ["product", "single"],
      ["single", "product"],
    ];
    const [type1, type2] = pick(patterns);

    term1 = makeTerm(type1 === "product");
    term1.factors.forEach((f) => {
      usedPairsMap.set(factorKey(f.func, f.angle), f);
    });

    term2 = makeTerm(type2 === "product");
    term2.factors.forEach((f) => {
      usedPairsMap.set(factorKey(f.func, f.angle), f);
    });

    sign = pick(["+", "-"]);

    // unikamy T - T
    if (sign === "-" && term1.latex === term2.latex) {
      continue;
    }

    value =
      term1.value + (sign === "+" ? 1 : -1) * term2.value;

    const signLatex = sign === "+" ? " + " : " - ";
    exprLatex = `${term1.latex}${signLatex}${term2.latex}`;
    exprNumericLatex = `${term1.numericLatex}${signLatex}${term2.numericLatex}`;
    exprAfterProductsLatex = `${term1.afterLatex}${signLatex}${term2.afterLatex}`;

    // próbujemy symbolicznie zsumować, gdy oba składniki mają ten sam pierwiastek
    if (term1.symRad === term2.symRad) {
      const rad = term1.symRad;
      const n1 = term1.symRatNum;
      const d1 = term1.symRatDen;
      const n2 = term2.symRatNum;
      const d2 = term2.symRatDen;

      const commonDen = d1 * d2;
      const s = sign === "+" ? 1 : -1;
      const numCombined = n1 * d2 + s * n2 * d1;

      exprCombinedLatex = makeLatexFromSymbolic(numCombined, commonDen, rad);
    } else {
      exprCombinedLatex = null;
    }

    // ograniczamy się do |wynik| ≤ 1
    if (Math.abs(value) > 1 + 1e-6) {
      continue;
    }

    correct = snapToKnown(value);
    if (!correct) {
      continue;
    }

    break;
  }

  // dobór dystraktorów z ograniczeniem na ujemne przy dodawaniu
  const basePool = KNOWN_RESULTS.filter(
    (item) => Math.abs(item.key - correct.key) > 1e-6
  );

  let distractors;
  if (sign === "+" && value > 0) {
    const positives = basePool.filter((d) => d.key > 0);
    const negatives = basePool.filter((d) => d.key < 0);

    distractors = [];

    // losowo: 0 lub 1 ujemna
    if (negatives.length > 0 && Math.random() < 0.5) {
      distractors.push(pick(negatives));
    }

    const needPos = 3 - distractors.length;
    const posShuffled = shuffle(positives);
    distractors.push(...posShuffled.slice(0, needPos));
    distractors = shuffle(distractors);
  } else {
    distractors = shuffle(basePool).slice(0, 3);
  }

  const rawOptions = shuffle([correct, ...distractors]);

  const options = rawOptions.map((opt, idx) => ({
    label: LETTERS[idx],
    value: opt.key,
    latex: opt.latex,
    isCorrect: Math.abs(opt.key - correct.key) < 1e-6,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  const usedPairs = Array.from(usedPairsMap.values()).map((f) => ({
    func: f.func,
    angle: f.angle,
    numLatex: f.numLatex,
  }));

  return {
    type: "MaturaTrigProductSumClosedTask",
    data: {
      exprLatex,
      exprNumericLatex,
      exprAfterProductsLatex,
      exprCombinedLatex,
      value,
      correctLatex: correct.latex,
      options,
      correctIndex,
      usedPairs,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={exprLatex} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= ODPOWIEDŹ UŻYTKOWNIKA =======================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const {
    usedPairs,
    exprLatex,
    exprNumericLatex,
    exprAfterProductsLatex,
    exprCombinedLatex,
    correctLatex,
  } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Odczytujemy wartości funkcji
        trygonometrycznych dla użytych kątów z tabeli kątów szczególnych.
      </p>

      {usedPairs.map((p, idx) => (
        <BlockMath
          key={`${p.func}-${p.angle}-${idx}`}
          math={`${FUNC_LATEX[p.func]}${p.angle}^\\circ = ${p.numLatex}`}
        />
      ))}

      <p>
        <strong>Krok 2.</strong> Podstawiamy te wartości do wyrażenia i
        wykonujemy mnożenia w poszczególnych składnikach.
      </p>
      <BlockMath math={exprLatex} />
      <BlockMath math={exprNumericLatex} />
      <BlockMath math={exprAfterProductsLatex} />

      <p>
        <strong>Krok 3.</strong> Sprowadzamy ułamki do wspólnego
        mianownika (jeśli trzeba) i dodajemy lub odejmujemy liczniki.
      </p>
      {exprCombinedLatex ? (
        <BlockMath
          math={`${exprAfterProductsLatex} = ${exprCombinedLatex}`}
        />
      ) : (
        <BlockMath
          math={`${exprAfterProductsLatex} = ${correctLatex}`}
        />
      )}
    </div>
  );
}
